import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Award, Factory, FlaskConical, ShieldCheck, Cog } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Quality = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  const qualityFeatures = [
    {
      icon: Factory,
      title: 'German Technology',
      description: 'State-of-the-art machinery from Germany ensuring superior quality and consistency in every product.'
    },
    {
      icon: FlaskConical,
      title: 'Testing Laboratory',
      description: 'Well-equipped in-house lab for comprehensive quality testing and product validation.'
    },
    {
      icon: ShieldCheck,
      title: 'Quality Assurance',
      description: '100% quality checked products with rigorous testing at every stage of production.'
    },
    {
      icon: Cog,
      title: 'Process Control',
      description: 'Stringent process controls and continuous monitoring to maintain consistent quality.'
    }
  ];

  const qcSteps = [
    {
      step: '1',
      title: 'Raw Material Inspection',
      description: 'Every batch of raw material is tested for quality, purity, and consistency before production.'
    },
    {
      step: '2',
      title: 'In-Process Quality Checks',
      description: 'Continuous monitoring during production to ensure adherence to specifications.'
    },
    {
      step: '3',
      title: 'Final Product Testing',
      description: 'Comprehensive testing of finished products including tensile strength, bulk, and color fastness.'
    },
    {
      step: '4',
      title: 'Packaging & Dispatch',
      description: 'Careful packaging and final inspection before dispatch to ensure product integrity.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-br from-teal-50/50 via-white to-orange-50/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-3xl" {...fadeInUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              Quality & <span className="text-primary">Infrastructure</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our commitment to quality is reflected in our state-of-the-art infrastructure, 
              advanced technology, and rigorous quality control processes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <img 
                src="/images/infrastructure-image.jpeg" 
                alt="Manufacturing Facility"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>
            <motion.div className="space-y-6" {...fadeInUp} transition={{ delay: 0.2 }}>
              <div>
                <h2 className="text-3xl sm:text-4xl font-semibold mb-4">World-Class Infrastructure</h2>
                <p className="text-lg text-muted-foreground">
                  Our manufacturing facility is equipped with cutting-edge German technology machines 
                  that make yarn the way it's meant to be made. Everything from our technical facilities 
                  to our production engineers has been designed to allow us to adjust our production to 
                  meet market demands quickly.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  'State-of-the-art German machinery',
                  'Advanced POY spinning technology',
                  'High-speed texturizing equipment',
                  'Automated quality control systems',
                  'Climate-controlled production environment',
                  'Efficient material handling systems'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Features */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">Our Quality Pillars</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Quality is not just a goal - it's embedded in every aspect of our operations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityFeatures.map((feature, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full text-center">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QC Process */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">Quality Control Process</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive QC process ensures every product meets the highest standards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qcSteps.map((step, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="relative h-full">
                  <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                    {step.step}
                  </div>
                  <CardHeader className="pt-8">
                    <CardTitle className="text-lg">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-4xl mx-auto text-center space-y-8" {...fadeInUp}>
            <div className="mx-auto w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
              <Award className="h-10 w-10" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold">Certifications & Compliance</h2>
            <p className="text-lg opacity-90">
              We maintain the highest standards of quality and comply with all relevant industry 
              regulations and international standards. Our commitment to quality has earned us the 
              trust of leading FIBC manufacturers across India and beyond.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              {[
                'ISO Certified',
                'Quality Tested',
                'Export Grade',
                'Industry Approved'
              ].map((cert, idx) => (
                <div key={idx} className="">
                  <div className="text-lg font-semibold">{cert}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Quality;
